import { useState, useEffect, useRef } from 'react';
import api, { BACKEND_URL } from '../api/axios';

export default function AttendanceControls() {
  const [status, setStatus] = useState('loading');
  const [breakType, setBreakType] = useState(null);
  const [breakTypes, setBreakTypes] = useState([]);
  const [showBreakMenu, setShowBreakMenu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showLateModal, setShowLateModal] = useState(false);
  const [lateReason, setLateReason] = useState('');
  const [lateMessage, setLateMessage] = useState('');
  const menuRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const res = await api.post('/agent/attendance/status');
      setStatus(res.data.status);
      setBreakType(res.data.break_type || null);
    } catch (err) {
      console.error('Failed to fetch attendance status', err);
      setStatus('not_checked_in');
    }
  };

  const fetchBreakTypes = async () => {
    try {
      const res = await api.post('/agent/attendance/break-types');
      const types = (res.data.break_types || []).map((bt) => bt.name);
      setBreakTypes(types);
    } catch (err) {
      console.error('Failed to fetch break types', err);
      setBreakTypes([]);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchBreakTypes();
  }, []);

  useEffect(() => {
    const interval = setInterval(fetchStatus, 15000);
    const handleFocus = () => fetchStatus();
    window.addEventListener('focus', handleFocus);
    return () => {
      clearInterval(interval);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowBreakMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runAction = async (fn, errMsg) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      alert(err.response?.data?.message || errMsg);
    } finally {
      await fetchStatus();
      setBusy(false);
    }
  };

  const handleCheckIn = async (reason = null) => {
    setBusy(true);
    try {
      await api.post('/agent/attendance/check-in', reason ? { late_reason: reason } : {});
      setShowLateModal(false);
      setLateReason('');
    } catch (err) {
      if (err.response?.data?.requires_reason === 'late_checkin') {
        setLateMessage(err.response.data.message);
        setShowLateModal(true);
      } else {
        alert(err.response?.data?.message || 'Check-in failed');
      }
    } finally {
      await fetchStatus();
      setBusy(false);
    }
  };

  const handleCheckOut = () => runAction(() => api.post('/agent/attendance/check-out'), 'Check-out failed');
  const handleBreakEnd = () => runAction(() => api.post('/agent/attendance/break-end'), 'Failed to end break');
  const handleBreakStart = (type) => {
    setShowBreakMenu(false);
    runAction(() => api.post('/agent/attendance/break-start', { break_type: type }), 'Failed to start break');
  };

  if (status === 'loading') {
    return <div className="w-28 h-9 rounded-full bg-gray-100 animate-pulse" />;
  }

  const isCheckedIn = status === 'checked_in';
  const isOnBreak = status === 'on_break';
  const isCheckedOut = status === 'checked_out';
  const canCheckIn = !isCheckedIn && !isOnBreak && !isCheckedOut;

  return (
    <div className="flex items-center gap-2">
      {canCheckIn && (
        <button
          onClick={() => handleCheckIn()}
          disabled={busy}
          className="flex items-center gap-2 text-sm font-semibold text-white bg-emerald-600 pl-3 pr-4 py-2 rounded-full hover:bg-emerald-700 disabled:opacity-50 transition-colors shadow-sm"
        >
          <span className="w-2 h-2 rounded-full bg-white" />
          Check In
        </button>
      )}

      {isCheckedIn && (
        <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full pl-1 pr-1 py-1">
          <span className="flex items-center gap-1.5 pl-2 pr-1 text-xs font-medium text-emerald-700">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            On duty
          </span>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowBreakMenu((v) => !v)}
              disabled={busy || breakTypes.length === 0}
              className="text-sm font-semibold text-amber-700 bg-white border border-amber-200 px-3 py-1.5 rounded-full hover:bg-amber-50 disabled:opacity-50 transition-colors flex items-center gap-1"
            >
              Break
              <svg width="11" height="11" viewBox="0 0 12 12" className={`transition-transform ${showBreakMenu ? 'rotate-180' : ''}`}>
                <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            {showBreakMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-lg py-1 z-30 max-h-64 overflow-y-auto">
                {breakTypes.length === 0 && (
                  <div className="px-4 py-2 text-sm text-gray-400">No break types configured</div>
                )}
                {breakTypes.map((type) => (
                  <button
                    key={type}
                    onClick={() => handleBreakStart(type)}
                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={handleCheckOut}
            disabled={busy}
            className="text-sm font-semibold text-white bg-red-600 px-3 py-1.5 rounded-full hover:bg-red-700 disabled:opacity-50 transition-colors"
          >
            Check Out
          </button>
        </div>
      )}

      {isOnBreak && (
        <button
          onClick={handleBreakEnd}
          disabled={busy}
          className="flex items-center gap-2 text-sm font-semibold text-white bg-amber-500 pl-3 pr-4 py-2 rounded-full hover:bg-amber-600 disabled:opacity-50 transition-colors shadow-sm"
        >
          <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
          End {breakType || 'Break'}
        </button>
      )}

      {isCheckedOut && (
        <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-full pl-3 pr-4 py-2">
          <span className="w-2 h-2 rounded-full bg-blue-500" />
          <span className="text-sm font-semibold text-blue-700">Day complete — see you tomorrow!</span>
        </div>
      )}

      {showLateModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-5 w-96 shadow-xl">
            <h4 className="font-semibold text-sm mb-2">Late Check-In</h4>
            <p className="text-xs text-gray-500 mb-3">{lateMessage}</p>
            <textarea
              value={lateReason}
              onChange={(e) => setLateReason(e.target.value)}
              className="w-full border border-gray-200 rounded-lg p-2 text-sm mb-3"
              rows={3}
              placeholder="Reason for late check-in..."
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowLateModal(false)} className="text-sm px-3 py-1.5 rounded-lg border">Cancel</button>
              <button
                onClick={() => handleCheckIn(lateReason)}
                disabled={!lateReason.trim()}
                className="text-sm px-3 py-1.5 rounded-lg bg-emerald-600 text-white disabled:opacity-50"
              >
                Submit & Check In
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
